<script>
export default {
    page() {
        const title = `Account - ${this.isPassSet ? 'Set' : 'Change'} Password`
        const description = `Account - ${this.isPassSet ? 'Set' : 'Change'} Password`
        return {
            title,
            meta: [
                { name: 'title', vmid: 'title', content: title },
                { name: 'description', vmid: 'description', content: description },

                { property: 'og:title', vmid: 'og:title', content: title },
                { property: 'og:description', vmid: 'og:description', content: description },

                { property: 'twitter:title', vmid: 'twitter:title', content: title },
                { property: 'twitter:description', vmid: 'twitter:description', content: description },
            ],
        }
    },
}
</script>

<script setup>
import { computed, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router/composables'
import DataLayer from '@services/data-layer/index'
import { required, sameAs, useFormValidation } from '@composables/use-form-validation'
import { useStore } from '@composables/use-store'
import { useMessages } from '@composables/use-messages'

const getMessageText = useMessages()
const router = useRouter()
const store = useStore()
const user = ref(null)
const password = ref('')
const newPassword = ref('')
const newPasswordConfirmation = ref('')
const member = computed(() => store.state.member.currentMember)
const isPassSet = computed(() => store.state.member.passState === 'set')
const crumbs = computed(() => [
    { label: 'Account Management', to: '/account' },
    { label: `${isPassSet.value ? 'Set' : 'Change'} Password` },
])
const validation = useFormValidation({
    password: { value: password, validators: [required] },
    newPassword: { value: newPassword, validators: [required] },
    newPasswordConfirmation: {
        value: newPasswordConfirmation,
        validators: [required, sameAs(() => newPassword)],
    },
})

function notify(messageKey, type) {
    store.dispatch('appearance/handleNotification', {
        message: getMessageText(messageKey),
        type,
    })
}

function redirectToAccountPage() {
    router.push('/account')
}

function changePassword() {
    validation.$touch()
    if (!required(newPassword.value) || !required(newPasswordConfirmation.value)) {
        return undefined
    }
    if (newPasswordConfirmation.value !== newPassword.value) {
        notify('PASSWORD_MATCH', 'danger')
        return undefined
    }

    user.value.password = newPassword.value
    return DataLayer.members
        .edit({ id: user.value.id, data: user.value })
        .then(response => {
            store.commit('member/SET_CURRENT_MEMBER', response.body)
            store.commit('member/SET_PASS_STATE', 'set')
            notify(isPassSet.value ? 'PASSWORD_UPDATED' : 'PASSWORD_SET', 'success')
            redirectToAccountPage()
        })
        .catch(() => {
            notify('SOMETHING_WENT_WRONG', 'danger')
        })
}

function checkPassword() {
    if (!isPassSet.value) {
        return changePassword()
    }

    validation.$touch()
    if (
        validation.password.$invalid ||
        validation.newPassword.$invalid ||
        !required(newPasswordConfirmation.value)
    ) {
        notify('MISSING_REQUIRED_FIELDS', 'danger')
        return undefined
    }

    return DataLayer.auth
        .login({
            email: member.value.email,
            password: password.value,
        })
        .then(changePassword)
        .catch(error => {
            if (error.response.status === 401) {
                notify('WRONG_PASSWORD', 'danger')
            }
        })
}

onMounted(() => {
    user.value = { ...store.state.member.currentMember }
})
</script>

<template>
    <div>
        <BaseBreadcrumbs :crumbs="crumbs"/>
        <h1 class="title">
            {{ isPassSet ? 'Change Password' : 'Set Password' }}
        </h1>
        <label
            v-if="!isPassSet"
            class="subtitle"
        >Setting a password will allow you to login using your email.</label>
        <form
            class="form"
            autocomplete="off"
            @submit.prevent="checkPassword"
        >
            <div class="form-container">
                <div
                    v-if="isPassSet"
                    class="field-container"
                >
                    <label class="label">Current Password</label>
                    <BaseInput
                        v-model="password"
                        :class="['field', validation.password.$error && 'error']"
                        type="password"
                        autocomplete="password"
                        placeholder="Enter current password"
                        @blur="validation.password.$touch()"
                    />
                </div>
                <div class="field-container">
                    <label class="label">New Password</label>
                    <BaseInput
                        v-model="newPassword"
                        :class="['field', validation.newPassword.$error && 'error']"
                        type="password"
                        autocomplete="new-password"
                        placeholder="Enter new password"
                        aria-autocomplete="none"
                        @blur="validation.newPassword.$touch()"
                    />
                </div>
                <div class="field-container">
                    <label class="label">Confirm New Password</label>
                    <BaseInput
                        v-model="newPasswordConfirmation"
                        :class="['field', validation.newPasswordConfirmation.$error && 'error']"
                        type="password"
                        autocomplete="new-password"
                        placeholder="Enter new password again"
                        aria-autocomplete="none"
                        @blur="validation.newPasswordConfirmation.$touch()"
                    />
                </div>
            </div>
            <div class="actions">
                <BaseButton
                    class="cancel-button"
                    button-type="transparent"
                    size="medium"
                    type="button"
                    @click="redirectToAccountPage"
                >Cancel</BaseButton>
                <BaseButton
                    class="save-button"
                    button-type="tagBlack"
                    size="medium"
                    type="submit"
                    @click="() => {}"
                >{{ isPassSet ? 'Change password' : 'Set password' }}</BaseButton>
            </div>
        </form>
    </div>
</template>

<style lang="scss" scoped>
@import '@design';

.title {
    margin-top: 0;
}

.subtitle {
    @extend %contentSubtitleM;

    display: block;
    margin-bottom: $s-xxl;
}

.form-container {
    display: flex;
    flex-direction: column;
    gap: $s-l;
}

.field-container {
    display: flex;
    flex-direction: column;
    gap: $s-m;
}

.label {
    @extend %contentSubtitleM;
}

.birthday-fields {
    display: grid;
    grid-template-columns: 2fr 3fr 2fr;
    gap: $s-xs;
}

.actions {
    display: flex;
    gap: $s-xl;
    align-items: center;
    justify-content: center;
    margin-top: $s-xxl;

    .save-button {
        padding: $s-m $s-l;
        font-weight: unset;
        text-transform: unset;
    }
}

@include tablet {
    .birthday-fields {
        grid-template-columns: 1fr 2fr 4fr;
        gap: $s-s;
    }

    .actions {
        justify-content: flex-end;
    }
}
</style>
